/**
 * Favorite accounts service.
 * Fetches and mutates the authenticated user's favorited accounts via the Mackinac XRPC server.
 */

import type { Agent } from '@atproto/api'
import { MACKINAC_XRPC_URL, MACKINAC_SERVICE_DID } from '@/constants/api'

const GET_NSID = 'app.mackinac.getFavoriteAccounts'
const ADD_NSID = 'app.mackinac.addFavoriteAccount'
const REMOVE_NSID = 'app.mackinac.removeFavoriteAccount'

export interface FavoriteAccount {
    did: string
    favoritedAt: string
}

async function getServiceAuthToken(agent: Agent, lxm: string): Promise<string> {
    const { data } = await agent.com.atproto.server.getServiceAuth({
        aud: MACKINAC_SERVICE_DID,
        lxm,
    })
    return data.token
}

/**
 * Fetch the authenticated user's favorited accounts.
 * Throws on HTTP errors so React Query preserves cached data on re-fetch failure.
 * Returns an empty array only for non-HTTP errors (network, auth token failures).
 */
export async function fetchFavoriteAccounts(agent: Agent): Promise<FavoriteAccount[]> {
    try {
        const token = await getServiceAuthToken(agent, GET_NSID)
        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${GET_NSID}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) throw new Error(`getFavoriteAccounts failed: ${response.status}`)
        const body = await response.json()
        if (!Array.isArray(body.accounts)) return []
        return (body.accounts as unknown[]).filter(
            (a): a is FavoriteAccount =>
                typeof a === 'object' &&
                a !== null &&
                typeof (a as FavoriteAccount).did === 'string' &&
                typeof (a as FavoriteAccount).favoritedAt === 'string'
        )
    } catch {
        return []
    }
}

/**
 * Add an account to the authenticated user's favorites.
 * Throws on error so callers can handle optimistic-update rollback.
 */
export async function addFavoriteAccount(agent: Agent, did: string): Promise<void> {
    const token = await getServiceAuthToken(agent, ADD_NSID)
    const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${ADD_NSID}`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ did }),
    })
    if (!response.ok) {
        throw new Error(`addFavoriteAccount failed: ${response.status}`)
    }
}

/**
 * Remove an account from the authenticated user's favorites.
 * Throws on error so callers can handle optimistic-update rollback.
 */
export async function removeFavoriteAccount(agent: Agent, did: string): Promise<void> {
    const token = await getServiceAuthToken(agent, REMOVE_NSID)
    const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${REMOVE_NSID}`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ did }),
    })
    if (!response.ok) {
        throw new Error(`removeFavoriteAccount failed: ${response.status}`)
    }
}
